import React, { useState, useEffect } from 'react';
import { Modal, TextInput, Select, Button, Stack, Text, Group } from '@mantine/core';
import { registryApi } from '../apis/registry.api';
import { subscriptionApi } from '../apis/subscription.api';
import { toast } from 'react-toastify';

const OrgSettings = ({ authToken, organizationId, onClose, currentUsers, currentOrgLeader, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [selectedNewLeader, setSelectedNewLeader] = useState('');

  // Filter active users from the passed currentUsers prop
  const activeUsers = currentUsers.filter(user => user.status === 'active');

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        setLoading(true);
        const orgDetails = await registryApi.getOrganizationDetails(authToken, organizationId);
        const orgInfo = orgDetails[0];
        console.log('orgInfo', orgInfo);
        setFormData({
          orgName: orgInfo.orgName || '',
          orgBaseRegion: orgInfo.orgBaseRegion || '',
          orgAltRegions: orgInfo.orgAltRegions || [],
          totalUsers: orgInfo.totalUsers || 0,
          createdAt: orgInfo.createdAt,
          updatedAt: orgInfo.updatedAt
        });
      } catch (err) {
        console.error('Error fetching org details:', err);
        setError('Failed to load organization details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, [authToken, organizationId]);

  const handleLeaderChange = async () => {
    if (!selectedNewLeader) {
      toast.error('Please select a user to be the new leader');
      return;
    }

    try {
      setLoading(true);
      await subscriptionApi.changeSubscriptionLeaderByEmail(
        authToken,
        organizationId,
        selectedNewLeader
      );
      toast.success('Organization leader updated successfully');
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating organization leader:', err);
      toast.error('Failed to update organization leader');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Organization Settings"
      size="lg"
      styles={{
        header: {
          backgroundColor: '#f8f9fa',
          padding: '16px 24px',
          marginBottom: '16px',
        },
        title: {
          fontWeight: 600,
          fontSize: '1.1rem',
        }
      }}
    >
      {loading && !formData ? (
        <Text p="md">Loading organization details...</Text>
      ) : error ? (
        <Text color="red" p="md">{error}</Text>
      ) : formData && (
        <Stack spacing="md">
          <div>
            <Text size="sm" weight={500} color="dimmed">Organization Name</Text>
            <Text>{formData.orgName}</Text>
          </div>
          
          <div>
            <Text size="sm" weight={500} color="dimmed">Base Region</Text>
            <Text>{formData.orgBaseRegion}</Text>
          </div>
          
          <div>
            <Text size="sm" weight={500} color="dimmed">Alternate Regions</Text>
            <Text>{formData.orgAltRegions?.join(', ') || 'None'}</Text>
          </div>
          
          <div>
            <Text size="sm" weight={500} color="dimmed">Total Users</Text>
            <Text>{formData.totalUsers}</Text>
          </div>

          <div>
            <Text size="sm" weight={500} color="dimmed">Current Organization Leader</Text>
            <Text>{currentOrgLeader?.email || 'Not set'}</Text>
          </div>

          <div>
            <Text size="sm" weight={500} color="dimmed" mb={8}>Change Organization Leader</Text>
            {activeUsers.length > 0 ? (
              <Select
                data={activeUsers.map(user => ({
                  value: user.email,
                  label: user.email
                }))}
                value={selectedNewLeader}
                onChange={setSelectedNewLeader}
                placeholder="Select new organization leader"
                searchable
                clearable
                disabled={loading}
              />
            ) : (
              <Text color="dimmed">No active users available</Text>
            )}
          </div>
          
          <Group position="right" mt="xl">
            <Button 
              variant="filled" 
              color="blue"
              onClick={handleLeaderChange}
              loading={loading}
              disabled={!selectedNewLeader || selectedNewLeader === currentOrgLeader?.email}
            >
              Update Leader
            </Button>
            <Button variant="default" onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default OrgSettings;